<template>
  <v-dialog v-model="visible" max-width="600px" persistent>
    <v-card>
      <v-toolbar color="primary" dark flat>
        <v-toolbar-title>{{ getAppMode === 'legal' ? 'New Billing Entry' : 'Add Item' }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="pt-6">
        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="entry.description"
                :items="['Fee', 'Disbursement']"
                label="Entry Type"
                outlined
                dense
                required
                :rules="[v => !!v || 'Required']"
                @change="handleTypeChange"
              ></v-select>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="entry.date"
                label="Date"
                type="date"
                outlined
                dense
                required
              ></v-text-field>
            </v-col>

            <v-col cols="12">
              <AutoCompleteCrudAdvanced
                table="services"
                :label="getAppMode === 'legal' ? 'Billing Description' : 'Item Description'"
                @input="getSelectedService"
              ></AutoCompleteCrudAdvanced>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="entry.quantity"
                label="Quantity"
                type="number"
                min="1"
                step="any"
                outlined
                dense
                required
                :rules="[v => v > 0 || 'Must be > 0']"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="entry.price"
                label="Unit Price (Excl. VAT)"
                :prefix="getCurrency"
                type="number"
                outlined
                dense
                required
                @change="updateServicePrice()"
                :rules="[v => v >= 0 || 'Invalid price']"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4" class="d-flex align-center">
              <v-checkbox
                v-model="entry.isVatable"
                :label="`Apply ${getVat.label} VAT`"
                hide-details
                class="mt-0"
              ></v-checkbox>
            </v-col>
          </v-row>

          <v-alert color="grey lighten-4" class="mt-4 pa-3" dense outlined>
            <v-row no-gutters class="caption text-uppercase font-weight-bold grey--text text--darken-1 mb-1">
              <v-col cols="4">Subtotal</v-col>
              <v-col cols="4" class="text-center">VAT ({{ getVat.label }})</v-col>
              <v-col cols="4" class="text-right">Total</v-col>
            </v-row>
            <v-row no-gutters class="subtitle-2">
              <v-col cols="4">
                {{ getCurrency }} {{ subtotal.toFixed(2) }}
              </v-col>
              <v-col cols="4" class="text-center">
                {{ getCurrency }} {{ calculateVAT.toFixed(2) }}
              </v-col>
              <v-col cols="4" class="text-right primary--text font-weight-bold">
                {{ getCurrency }} {{ calculateTotal.toFixed(2) }}
              </v-col>
            </v-row>
          </v-alert>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="close">Cancel</v-btn>
        <v-btn color="success" :disabled="!valid" @click="save">
          <v-icon left>mdi-check</v-icon> Add to Ledger
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import helpersJs from "@/plugins/helpers";
import AutoCompleteCrudAdvanced from "@/modules/CrudDatabases/AutoCompleteCrudAdvanced.vue";

export default {
  props: {
    value: Boolean
  },
  components: { AutoCompleteCrudAdvanced },
  data() {
    return {
      valid: false,
      entryID: null,
      entry: {
        category_id: null,
        description: 'Fee',
        date: new Date().toISOString().substr(0, 10),
        quantity: 1,
        name: '',
        price: 0,
        isVatable: true,
        tax_amount: 0
      }
    };
  },
  computed: {
    getAppMode() {
      return this.$store.getters.getAppMode;
    },
    visible: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); }
    },
    subtotal() {
      const price = Number(this.entry.price) || 0;
      const qty = Number(this.entry.quantity) || 0;
      return price * qty;
    },
    getUserCompany() {
      return this.$store.getters.getUserCompany;
    },
    getVat() {
      return this.$store.getters.getVat;
    },
    calculateVAT() {
      if (this.entry.isVatable) {
        return this.subtotal * this.getVat.value;
      }
      return 0;
    },
    calculateTotal() {
      return this.subtotal + this.calculateVAT;
    },
    getCurrency() {
      return this.$store.getters.getCurrency || 'R';
    }
  },
  methods: {
    getSelectedService(selectedService) {
      if (!selectedService) return;
      this.entryID = selectedService.id;
      this.entry.name = selectedService.name;
      this.entry.price = parseFloat(selectedService.price) || 0;
      this.entry.category_id = selectedService.category_id;
    },
    async updateServicePrice() {
      if (!this.entry || !this.entryID) return;
      await helpersJs.updateModelSingleRow('services', 'price', this.entryID, this.entry.price);
    },
    handleTypeChange(val) {
      this.entry.isVatable = (val === 'Fee');
    },
    close() {
      this.visible = false;
      this.resetForm();
    },
    resetForm() {
      this.entryID = null;
      this.entry = {
        category_id: null,
        description: 'Fee',
        date: new Date().toISOString().substr(0, 10),
        quantity: 1,
        name: '',
        price: 0,
        isVatable: true,
        tax_amount: 0
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    save() {
      if (this.$refs.form.validate()) {
        const payload = JSON.parse(JSON.stringify(this.entry));
        payload.tax_amount = this.calculateVAT;
        payload.subtotal = this.subtotal;
        payload.total = this.calculateTotal;

        if(!payload.name){
          this.$toast.info("Select Item to Add!")
          return;
        }
        this.$emit('save', payload);
        this.close();
      }
    }
  }
};
</script>